if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          scrollIndicator: this.querySelector('[data-thumbnail-scroll-indicator]'),
          scrollIndicatorUp: this.querySelector('[data-thumbnail-scroll-indicator-up]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          
          // Hover para cambiar imagen sin click (solo en desktop)
          mediaToSwitch
            .querySelector('button')
            .addEventListener('mouseenter', this.onThumbnailHover.bind(this, mediaToSwitch.dataset.target));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize scroll indicator functionality
        this.initScrollIndicator();
      }

      initScrollIndicator() {
        if (!this.elements.thumbnails) return;
        
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;

        // Click to scroll down (flecha abajo)
        if (this.elements.scrollIndicator) {
          this.elements.scrollIndicator.addEventListener('click', () => {
            const scrollAmount = thumbnailList.clientHeight * 0.8;
            thumbnailList.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          });
        }

        // Click to scroll up (flecha arriba)
        if (this.elements.scrollIndicatorUp) {
          this.elements.scrollIndicatorUp.addEventListener('click', () => {
            const scrollAmount = thumbnailList.clientHeight * 0.8;
            thumbnailList.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
          });
        }

        // Show/hide based on scroll position
        thumbnailList.addEventListener('scroll', () => {
          this.updateScrollIndicatorVisibility(thumbnailList);
        });

        // Initial check - wait for layout to be computed
        // Run multiple checks to ensure proper visibility detection
        const checkVisibility = () => {
          this.updateScrollIndicatorVisibility(thumbnailList);
        };
        
        // Immediate check
        checkVisibility();
        // Delayed checks to handle late layout calculations
        setTimeout(checkVisibility, 100);
        setTimeout(checkVisibility, 500);
      }

      updateScrollIndicatorVisibility(thumbnailList) {
        // Check if content is scrollable (scrollHeight > clientHeight)
        const isScrollable = thumbnailList.scrollHeight > thumbnailList.clientHeight + 5;
        // Check if we're at the top
        const isAtTop = thumbnailList.scrollTop <= 10;
        // Check if we're at the bottom
        const isAtBottom = thumbnailList.scrollHeight - thumbnailList.scrollTop <= thumbnailList.clientHeight + 10;
        
        // Flecha hacia abajo: mostrar si es scrollable y NO está en el fondo
        if (this.elements.scrollIndicator) {
          const shouldHideDown = !isScrollable || isAtBottom;
          this.elements.scrollIndicator.classList.toggle('is-hidden', shouldHideDown);
        }

        // Flecha hacia arriba: mostrar si es scrollable y NO está en el tope
        if (this.elements.scrollIndicatorUp) {
          const shouldHideUp = !isScrollable || isAtTop;
          this.elements.scrollIndicatorUp.classList.toggle('is-hidden', shouldHideUp);
        }
      }

      // Método para manejar hover en thumbnails
      onThumbnailHover(mediaId) {
        // Solo activar en desktop
        if (!this.mql.matches) return;
        this.setActiveMedia(mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
